import { useState } from 'react';
import Modal from './Modal.jsx';
import { useAuthStore, useUiStore } from '../lib/stores.js';
import { authApi, ApiError } from '../lib/api.js';
import styles from './AuthModals.module.css';

export default function AuthModals() {
  const modal = useUiStore((s) => s.modal);
  const closeModal = useUiStore((s) => s.closeModal);
  if (modal === 'login') return <LoginModal onClose={closeModal} />;
  if (modal === 'register') return <RegisterModal onClose={closeModal} />;
  if (modal === 'reset') return <PasswordResetModal onClose={closeModal} />;
  return null;
}

function useAuthForm(initial) {
  const [data, setData] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));
  return { data, setData, busy, setBusy, error, setError, set };
}

function LoginModal({ onClose }) {
  const setSession = useAuthStore((s) => s.setSession);
  const openModal = useUiStore((s) => s.openModal);
  const f = useAuthForm({ email: '', password: '' });
  // Set to true when the API rejects login with `email_not_verified`. We
  // swap the form for a "check your email" panel + resend button instead
  // of pretending the password was wrong.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    f.setError('');
    f.setBusy(true);
    try {
      const res = await authApi.login(f.data.email, f.data.password);
      setSession({ token: res.session.token, user: res.user, profiles: res.profiles });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'email_not_verified') {
        setNeedsVerification(true);
      } else {
        f.setError(err instanceof ApiError ? err.message : 'Could not sign in');
      }
    } finally {
      f.setBusy(false);
    }
  };

  const resend = async () => {
    setResendMessage('');
    setResendBusy(true);
    try {
      await authApi.requestEmailVerify(f.data.email);
      setResendMessage('Verification email re-sent.');
    } catch (err) {
      setResendMessage(err instanceof ApiError ? err.message : 'Could not resend');
    } finally {
      setResendBusy(false);
    }
  };

  if (needsVerification) {
    return (
      <Modal title="Verify your email" onClose={onClose}>
        <div className={styles.done}>
          <p className="muted">
            We sent a confirmation link to <strong>{f.data.email}</strong>. Click it to verify your address before signing in.
          </p>
          {resendMessage && <p className="muted">{resendMessage}</p>}
          <button type="button" className="btn" disabled={resendBusy} onClick={resend}>
            {resendBusy ? 'Sending…' : 'Resend email'}
          </button>
          <button type="button" className="btn primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Sign in" onClose={onClose}>
      <form onSubmit={submit} className={styles.form}>
        <Field label="Email">
          <input className="input" type="email" autoFocus required value={f.data.email} onChange={f.set('email')} />
        </Field>
        <Field label="Password">
          <input className="input" type="password" required value={f.data.password} onChange={f.set('password')} />
        </Field>
        {f.error && <div className={styles.error}>{f.error}</div>}
        <button type="submit" className="btn primary" disabled={f.busy}>
          {f.busy ? 'Signing in…' : 'Sign in'}
        </button>
        <div className={styles.altLinks}>
          <button type="button" className={styles.link} onClick={() => openModal('reset')}>
            Forgot password?
          </button>
          <button type="button" className={styles.link} onClick={() => openModal('register')}>
            Create account
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RegisterModal({ onClose }) {
  const openModal = useUiStore((s) => s.openModal);
  const f = useAuthForm({ email: '', password: '', display_name: '', referral_code: '' });
  // 'form' → submit creates a PENDING_VERIFICATION user and the API
  // returns 202 with no session. We then show 'pending' until the user
  // clicks the link in their email (handled by /verify-email).
  const [stage, setStage] = useState('form');
  const [resendBusy, setResendBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    f.setError('');
    if (f.data.password.length < 10) { f.setError('Password must be at least 10 characters.'); return; }
    f.setBusy(true);
    try {
      await authApi.register(f.data);
      setStage('pending');
    } catch (err) {
      f.setError(err instanceof ApiError ? err.message : 'Could not create account');
    } finally {
      f.setBusy(false);
    }
  };

  const resend = async () => {
    setResendMessage('');
    setResendBusy(true);
    try {
      await authApi.requestEmailVerify(f.data.email);
      setResendMessage('Verification email re-sent.');
    } catch (err) {
      setResendMessage(err instanceof ApiError ? err.message : 'Could not resend');
    } finally {
      setResendBusy(false);
    }
  };

  if (stage === 'pending') {
    return (
      <Modal title="Check your email" onClose={onClose} size="md">
        <div className={styles.done}>
          <p className="muted">
            We sent a confirmation link to <strong>{f.data.email}</strong>. Click it to verify your address — your trial starts the moment you confirm.
          </p>
          {resendMessage && <p className="muted">{resendMessage}</p>}
          <button type="button" className="btn" disabled={resendBusy} onClick={resend}>
            {resendBusy ? 'Sending…' : 'Resend email'}
          </button>
          <button type="button" className="btn primary" onClick={onClose}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Create your VIBE TV account" onClose={onClose} size="md">
      <form onSubmit={submit} className={styles.form}>
        <Field label="Display name">
          <input className="input" autoFocus value={f.data.display_name} onChange={f.set('display_name')} />
        </Field>
        <Field label="Email">
          <input className="input" type="email" required value={f.data.email} onChange={f.set('email')} />
        </Field>
        <Field label="Password" hint="At least 10 characters">
          <input className="input" type="password" required minLength={10} value={f.data.password} onChange={f.set('password')} />
        </Field>
        <Field label="Referral code" hint="Optional">
          <input className="input" value={f.data.referral_code} onChange={f.set('referral_code')} />
        </Field>
        {f.error && <div className={styles.error}>{f.error}</div>}
        <button type="submit" className="btn primary" disabled={f.busy}>
          {f.busy ? 'Creating…' : 'Create account'}
        </button>
        <div className={styles.altLinks}>
          <button type="button" className={styles.link} onClick={() => openModal('login')}>
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PasswordResetModal({ onClose }) {
  const [stage, setStage] = useState('request'); // request | confirm | done
  const f = useAuthForm({ email: '', code: '', token: '', password: '' });

  const requestReset = async (e) => {
    e.preventDefault();
    f.setError(''); f.setBusy(true);
    try {
      await authApi.passwordResetRequest(f.data.email);
      setStage('confirm');
    } catch (err) {
      f.setError(err instanceof ApiError ? err.message : 'Could not request reset');
    } finally {
      f.setBusy(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    f.setError('');
    if (f.data.password.length < 10) { f.setError('Password must be at least 10 characters.'); return; }
    f.setBusy(true);
    try {
      const body = f.data.token
        ? { token: f.data.token, password: f.data.password }
        : { email: f.data.email, code: f.data.code, password: f.data.password };
      await authApi.passwordResetConfirm(body);
      setStage('done');
    } catch (err) {
      f.setError(err instanceof ApiError ? err.message : 'Could not confirm reset');
    } finally {
      f.setBusy(false);
    }
  };

  return (
    <Modal title="Reset your password" onClose={onClose} size="md">
      {stage === 'request' && (
        <form onSubmit={requestReset} className={styles.form}>
          <p className={styles.lede}>Enter your email and we'll send you a reset code.</p>
          <Field label="Email">
            <input className="input" type="email" autoFocus required value={f.data.email} onChange={f.set('email')} />
          </Field>
          {f.error && <div className={styles.error}>{f.error}</div>}
          <button type="submit" className="btn primary" disabled={f.busy}>
            {f.busy ? 'Sending…' : 'Send reset code'}
          </button>
        </form>
      )}
      {stage === 'confirm' && (
        <form onSubmit={confirmReset} className={styles.form}>
          <p className={styles.lede}>Check your email for a 6-digit code, or paste the reset token from the link.</p>
          <Field label="6-digit code">
            <input className="input" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={f.data.code} onChange={f.set('code')} />
          </Field>
          <div className={styles.divider}>or</div>
          <Field label="Reset token (from email link)">
            <input className="input" value={f.data.token} onChange={f.set('token')} />
          </Field>
          <Field label="New password" hint="At least 10 characters">
            <input className="input" type="password" required minLength={10} value={f.data.password} onChange={f.set('password')} />
          </Field>
          {f.error && <div className={styles.error}>{f.error}</div>}
          <button type="submit" className="btn primary" disabled={f.busy}>
            {f.busy ? 'Confirming…' : 'Set new password'}
          </button>
        </form>
      )}
      {stage === 'done' && (
        <div className={styles.done}>
          <h3>Password updated</h3>
          <p className="muted">All your sessions were signed out. Sign in with the new password.</p>
          <button className="btn primary" onClick={onClose}>Done</button>
        </div>
      )}
    </Modal>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}{hint && <span className={styles.hint}> · {hint}</span>}</span>
      {children}
    </label>
  );
}
